import { cn } from "@/utils";
import { Message } from "@cohesive/api-client";
import { Markdown, RailStep } from "@cohesive/ui-components";
import { ChevronDown } from "lucide-react";
import { useMemo, useState } from "react";
import { latestThoughtLine } from "./harnessRailItems";

function formatThoughtLabel(ms: number): string {
  const seconds = Math.round(ms / 1000);
  return seconds === 0 ? "Thought briefly" : `Thought for ${seconds}s`;
}

/**
 * A consolidated Thoughts row: every adjacent thinking burst of the turn
 * merged into one item. While the model is still thinking the row shows the
 * latest line it wrote (the one live line of the rail when it is the tail);
 * once it has moved on the row reads "Thought for Ns" / "Thoughts". Clicking
 * reveals the full accumulated text. Collapsed by default and only the user
 * opens it.
 */
export function HarnessThoughts({
  messages,
  live,
}: {
  messages: Message[];
  /** This row is the tail of the live segment: shimmer its live line. */
  live: boolean;
}) {
  const [open, setOpen] = useState(false);

  const text = useMemo(
    () =>
      messages
        .map((m) => m.thinking_content?.trim() ?? "")
        .filter(Boolean)
        .join("\n\n"),
    [messages],
  );

  const last = messages[messages.length - 1];
  const streaming =
    last?.status === "streaming" && last.thinking_duration_milliseconds == null;
  const durationMs = messages.reduce(
    (sum, m) => sum + (m.thinking_duration_milliseconds ?? 0),
    0,
  );

  const label = streaming
    ? latestThoughtLine(text) || "Thinking…"
    : durationMs > 0
      ? formatThoughtLabel(durationMs)
      : messages.length > 1
        ? `Thoughts · ${messages.length}`
        : "Thoughts";

  if (!text) return null;

  return (
    <div className="px-4 py-2" data-testid="harness-thoughts">
      <RailStep icon={<span className="size-1.5 rounded-full bg-n-40" />} last>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="-ml-1 flex w-full min-w-0 items-center gap-1 text-left font-sans text-[13px] text-n-50 outline-none transition-colors duration-150 ease-out hover:text-n-70 focus-visible:text-n-70"
        >
          <span
            className={cn(
              "min-w-0 flex-1 truncate",
              streaming && live && "shimmer",
            )}
          >
            {label}
          </span>
          <ChevronDown
            className={cn(
              "size-3 shrink-0 transition-transform duration-150",
              open && "rotate-180",
            )}
          />
        </button>
        {open && (
          <Markdown className="mt-1.5 max-h-60 overflow-y-auto pr-1 font-sans text-xs text-n-50 text-pretty [&_p]:my-0 [&_p:not(:last-child)]:mb-2">
            {text}
          </Markdown>
        )}
      </RailStep>
    </div>
  );
}
